import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { NextStep } from "../lib/nextStep";
import { isRunInFlight, useGlobalRunStore } from "./useGlobalRunStore";
import { useNextStep } from "./useNextStep";

// passInFlight is the ONE spelling of "a pass is in flight" for the pages that
// read the spec read (0051 slices 2-3): the store follows a live run for the
// product, or the served step says working. While it holds, the spec column
// shows no layer reflection and no settled mark (0039 N4) and the drawer holds
// Amend, on the product page and on the map alike. useSpecFollow returns it;
// the product page's AnalyzedView applies it to the run signal and the step it
// is handed.
export function passInFlight(running: boolean, stepState: NextStep["state"] | undefined): boolean {
  return running || stepState === "working";
}

// useSpecFollow is how every page that reads the spec read (views/Product.tsx,
// views/Graph.tsx) follows the product's served next step (0051 slice 2): it
// reads the step (hooks/useNextStep, which refetches on window focus) and
// keeps the page's reads in step with it. When the served step's latest pass
// or state changes after the first served step (a pass that ended while this
// tab was not following it, read on focus), the spec read (under the tree
// key) and both queues refetch, so a result never shows over a stale spec or
// queues, and the map never offers Amend on a decision a pass already moved.
// A read already in flight (a save's own refetch) is reused, not restarted.
// The first served step, and the first step of another product, refetch
// nothing. It returns the step's read and the page's in-flight signal
// (passInFlight over the store's live run and the served step).
export function useSpecFollow(productId: string) {
  const queryClient = useQueryClient();
  const step = useNextStep(productId);
  const stepState = step.data?.state;
  const stepRecordId = step.data?.latest_analysis_record_id;
  const running = useGlobalRunStore((s) => isRunInFlight(s.runs, productId));

  const lastStep = useRef<{ productId: string; record: string; state: string } | null>(null);
  useEffect(() => {
    if (stepRecordId === undefined || stepState === undefined) return;
    const last = lastStep.current;
    lastStep.current = { productId, record: stepRecordId, state: stepState };
    if (last === null || last.productId !== productId) return;
    if (last.record === stepRecordId && last.state === stepState) return;
    const reuse = { cancelRefetch: false };
    void queryClient.invalidateQueries({ queryKey: ["product", productId, "tree"] }, reuse);
    void queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "questions"] }, reuse);
    void queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "decisions"] }, reuse);
  }, [productId, stepRecordId, stepState, queryClient]);

  return { step, inFlight: passInFlight(running, stepState) };
}
